import React, {
  FunctionComponent,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
} from 'react';

import { css } from '@emotion/react';
import CancelIcon from '@mui/icons-material/Cancel';

import { useOnClickOutside } from '@hooks/useOnClickOutside';

import { palette } from '@styles/theme';

import ModalPortal from './ModalPortal';

interface Props {
  open: boolean;
  handleOpen: () => void;
  width?: number;
  height?: number;
  title?: string | ReactNode | ReactElement;
  isOutsideClick?: boolean;
  children?: ReactNode | ReactElement;
}

const CommonModal: FunctionComponent<Props> = ({
  open,
  handleOpen,
  width,
  height,
  title,
  isOutsideClick = true,
  children,
}) => {
  const ref = useRef(null);

  useOnClickOutside(ref, () => isOutsideClick && handleOpen());

  useEffect(() => {
    const escKeyPress = (e: any) => {
      if (e.key === 'Escape') handleOpen();
    };
    window.addEventListener('keydown', escKeyPress);
    return () => window.removeEventListener('keydown', escKeyPress);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <ModalPortal>
      <div css={[container, open && openModal]}>
        <div css={modal(width, height)} ref={ref}>
          <div css={header}>
            <div css={closeBtn} onClick={handleOpen}>
              <CancelIcon />
            </div>
            {title}
          </div>
          <div css={contents}>{children && children}</div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default CommonModal;

const container = css`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.4);

  @keyframes modal-bg-show {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const openModal = css`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: modal-bg-show 0.3s;
`;

const modal = (width?: number, height?: number) => css`
  position: relative;
  width: ${width ? `${width}px` : 'auto'};
  height: ${height ? `${height}px` : 'auto'};
  background-color: #ffffff;
  box-shadow: 0px 5px 16px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
`;

const header = css`
  display: flex;
  position: relative;
  min-height: 72px;
  max-height: 94px;
  justify-content: flex-start;
  padding-left: 20px;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: #222;
  border-bottom: 1px solid ${palette.gray3};
`;

const closeBtn = css`
  position: absolute;
  top: 18px;
  right: 22px;
  cursor: pointer;
`;

const contents = css`
  display: flex;
  padding: 30px;
`;
